class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // La cuenta atras solo corre mientras el cajon esta abierto: fuera de ahi
    // nadie la ve y el intervalo seria trabajo tirado.
    CartFlashTimer.start();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Se para el intervalo, no la cuenta: el instante de fin vive en
    // sessionStorage, asi que al reabrir se reanuda donde tocaba.
    CartFlashTimer.stop();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // El innerHTML acaba de reemplazar el <span> del contador por el 10:00 que
    // pinta Liquid. Se repinta al momento con el valor real para que no se vea
    // el salto; el intervalo no se toca, porque busca el elemento en cada tick.
    CartFlashTimer.render();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Cuenta atras del anuncio del cajon de carrito.
 *
 * Marcado: <span id="cart-drawer-timer"> en snippets/cart-drawer.liquid.
 *
 * Dos decisiones que sostienen todo lo demas:
 *
 * 1. El estado es UN instante de fin en sessionStorage, no los segundos que
 *    quedan. Asi el refresco de pagina, la navegacion entre paginas y las
 *    actualizaciones AJAX no reinician nada: siempre se recalcula contra
 *    Date.now(). Al cerrar la pestana la sesion muere y la cuenta vuelve a
 *    empezar, que es el comportamiento que pide sessionStorage.
 *
 * 2. El elemento se busca en CADA tick con getElementById en vez de guardarse
 *    en una propiedad. Dawn reemplaza el innerHTML de .drawer__inner entero en
 *    cada cambio de cantidad, asi que cualquier referencia cacheada apuntaria a
 *    un nodo huerfano y el contador se congelaria tras el primer add-to-cart.
 *
 * Solo existe un intervalo (this.interval) y se limpia en stop(), al llegar a
 * cero y al ocultarse la pestana, asi que no se acumulan timers.
 */
const CartFlashTimer = {
  DURATION_MS: 10 * 60 * 1000,
  STORAGE_KEY: 'cartFlashTimerEndsAt',
  SELECTOR: 'cart-drawer-timer',

  interval: null,

  // Instante de fin de la sesion actual. Si no hay, o quedo basura, se abre uno
  // nuevo. sessionStorage puede lanzar en modo privado de algunos navegadores o
  // con cookies de terceros bloqueadas: si falla, el contador sigue funcionando
  // en memoria durante la vida de la pagina.
  endsAt() {
    try {
      const stored = parseInt(sessionStorage.getItem(this.STORAGE_KEY), 10);
      if (stored > 0) return stored;

      const fresh = Date.now() + this.DURATION_MS;
      sessionStorage.setItem(this.STORAGE_KEY, fresh);
      return fresh;
    } catch (error) {
      if (!this.memoryEndsAt) this.memoryEndsAt = Date.now() + this.DURATION_MS;
      return this.memoryEndsAt;
    }
  },

  // MM:SS con dos digitos siempre, acotado a cero por abajo.
  format(remainingMs) {
    const totalSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  },

  // Devuelve false cuando la cuenta ha terminado, para que tick() pueda parar.
  render() {
    const element = document.getElementById(this.SELECTOR);
    const remaining = this.endsAt() - Date.now();

    if (element) element.textContent = this.format(remaining);
    return remaining > 0;
  },

  tick() {
    // Al llegar a 00:00 se congela el texto y se suelta el intervalo. No se
    // borra la clave: mientras dure la sesion sigue en cero en vez de reiniciar
    // sola cada vez que se abre el cajon.
    if (!this.render()) this.stop();
  },

  start() {
    if (this.interval) return;
    if (!this.render()) return;

    this.interval = setInterval(() => this.tick(), 1000);
  },

  stop() {
    if (!this.interval) return;

    clearInterval(this.interval);
    this.interval = null;
  },
};

// Con la pestana en segundo plano el navegador estrangula los intervalos y el
// contador se desfasa. Se para al ocultarse y al volver se repinta contra el
// reloj, que es la unica fuente de verdad.
document.addEventListener('visibilitychange', () => {
  const drawer = document.querySelector('cart-drawer');
  const isOpen = drawer && drawer.classList.contains('active');

  if (document.hidden) {
    CartFlashTimer.stop();
  } else if (isOpen) {
    CartFlashTimer.start();
  }
});
